import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    OnDestroy,
    OnInit,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmDialogData } from '../confirm-dialog.interface';
import { DialogNavigationGuardService } from '../dialog-navigation-guard';

/**
 * A confirmation dialog that allows the user to confirm or deny a prompt.
 *
 * It will return `true` on confirmation and `false` for a denial.
 *
 * When `data.onConfirm` is provided, the dialog stays open while the async
 * action runs and shows a processing spinner on the confirm button.
 *
 * Usage:
 *
 ```
        dialog = inject(LcDialog);

        const ref = this.dialog.confirm({
          data: {
            confirmActionText: 'Yes',
            denyActionText: 'No',
            message: 'Do you want to post this announcement?'
          }
        });
 * ```
 *
 * If your use case is more complex, define a custom dialog component and use the `LcDialog` service to open it.
 */
@Component({
    selector: 'lc-confirm-dialog',
    templateUrl: './confirm-dialog.html',
    styleUrl: './confirm-dialog.scss',
    imports: [],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmDialogComponent implements OnInit, OnDestroy {
    private readonly destroyRef = inject(DestroyRef);
    private readonly navGuard = inject(DialogNavigationGuardService);
    protected readonly data = inject<ConfirmDialogData>(DIALOG_DATA);
    protected readonly dialogRef = inject<DialogRef<boolean | undefined>>(
        DialogRef<boolean | undefined>
    );

    protected readonly submitProcessing = signal(false);
    protected readonly isMessageArray = Array.isArray(this.data.message);
    protected readonly messageArray = this.isMessageArray ? (this.data.message as string[]) : [];

    protected readonly customBtnClass: string;

    constructor() {
        const custom = this.data.confirmButtonClass;
        if (custom) {
            this.customBtnClass =
                'btn btn-min-width ' + (Array.isArray(custom) ? custom.join(' ') : custom);
        } else {
            this.customBtnClass = 'btn btn-min-width btn-primary';
        }
    }

    ngOnInit(): void {
        if (this.data.onConfirm) {
            this.navGuard.blocked.set(true);
        }
        this.dialogRef.config.closePredicate = () => !this.submitProcessing();
    }

    ngOnDestroy(): void {
        this.navGuard.blocked.set(false);
    }

    protected onConfirmClick(): void {
        if (!this.data.onConfirm) {
            this.dialogRef.close(true);
            return;
        }

        this.submitProcessing.set(true);
        this.data
            .onConfirm()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                complete: () => {
                    this.submitProcessing.set(false);
                    this.dialogRef.close(true);
                },
                error: () => this.submitProcessing.set(false),
            });
    }
}
